import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Icon, Style, Stroke, Circle, Fill } from "ol/style.js";
import { fromLonLat } from "ol/proj";
import Overlay from "ol/Overlay";
import { QIMapLoader, QISpinner } from "../../../components";
import { useGetVehicleRoutingQuery, useGetFormattedVehicleRouteQuery } from "../services";
import {
  locationImg,
  locationImgNew,
  startPointMarker,
  endPointMarker,
  startPointMarkerNew,
  endPointMarkerNew,
  pointerNew,
  homeLocation,
  destination,
  direction,
  directionArrow,
} from "../assets";
import { data_point } from "../assets";
import moment from "moment";
import { removeDuplicates, getRotation, clusterStandstills, createClusterDialog, generateDirectionalArrowPoints } from "../utils";
import { calculateStandstills } from "../standstillCalculator";
import { createStandstillPopupElement } from "../../../components/Tracking/StandstillPopupContent";
import { toast } from "react-toastify";
import mapboxgl from "mapbox-gl";

// Helper function to create custom marker elements
const createCustomMarkerElement = (imageSrc) => {
  const element = document.createElement("div");
  element.className = "custom-marker";
  element.style.backgroundImage = `url(${imageSrc})`;
  element.style.width = "38px";
  element.style.height = "38px";
  element.style.backgroundSize = "contain";
  element.style.backgroundRepeat = "no-repeat";
  element.style.backgroundPosition = "center";
  return element;
};

/**
 * Component for displaying and tracking vehicle routes on a map.
 * @param {Object} props - Component props.
 * @param {string} props.dockAlign - Alignment of the map container.
 * @param {string} props.deviceId - ID of the device/vehicle.
 * @param {Object} props.realTimeData - Real-time data of the vehicle.
 * @param {function} props.setTrackMapRef - Function to set the map reference.
 * @param {Object} props.trackMapRef - Reference to the map.
 * @param {boolean} props.mapExpand - Whether the map is expanded.
 * @param {function} props.setResetCount - Function to reset a count.
 * @param {Object} props.selectedRange - Selected time range for data retrieval.
 * @param {function} props.maximizeMinimize - Function to maximize or minimize the map.
 */
export const VehicleTrack = ({
  dockAlign,
  deviceId,
  realTimeData,
  setTrackMapRef,
  trackMapRef,
  mapExpand,
  setResetCount,
  selectedRange,
  maximizeMinimize,
  mapProvider,
  mapboxAccessToken,
  showTooltip = false,
  tooltip,
  googleMapsApiKey,
  selectedVehiclePlate,
  type,
  clientConfig,
  trackingPoints,
  mapPoint,
}) => {
  const [trackData, setTrackData] = useState([]); // State to hold tracked vehicle data
  const [page, setPage] = useState(0); // Pagination page for fetched data
  const [totalCount] = useState(200); // Total count of data to fetch
  const [count, setCount] = useState(0); // Count for layer management
  const [initialLoad, setInitialLoad] = useState(true); // Flag to check if initial load
  const [isDrawing, setIsDrawing] = useState(false); // Flag to prevent concurrent draws
  const [needsRedraw, setNeedsRedraw] = useState(false); // Flag to indicate if redraw is needed
  const [isLiveData, setIsLiveData] = useState(false); // Flag to track if we're receiving real-time data
  const [maxHdopThreshold, setMaxHdopThreshold] = useState(100);
  const [userInteracting, setUserInteracting] = useState(false); // Track user interaction
  const [pendingFitLayer, setPendingFitLayer] = useState(null); // Track layer needing fit
  const [isLoading, setIsLoading] = useState(true); // Flag to track if data is still loading
  const [noDataAvailable, setNoDataAvailable] = useState(false); // Flag to track if no data is available
  const [allDataCollected, setAllDataCollected] = useState(false); // Flag to track if all data is collected
  const [lastKnownLocation, setLastKnownLocation] = useState(null); // Store last known location
  const interactionTimeoutRef = useRef(null);
  const liveDataTimeoutRef = useRef(null); // Timeout ref for live data debouncing
  const lastProcessedTrackResDataRef = useRef(null); // Track processed resData to avoid reprocessing stale data
  const [showToast, setShowToast] = useState(false); // Flag to control toast visibility
  const [hasSetInitialZoom, setHasSetInitialZoom] = useState(false); // Track if initial zoom has been set for mapPoint
  const [standstillData, setStandstillData] = useState([]); // Standstill data from API
  const [selectedStandstill, setSelectedStandstill] = useState(null); // Selected standstill for popup
  const [showStandstillPopup, setShowStandstillPopup] = useState(false); // Control popup visibility
  const standstillOverlaysRef = useRef([]); // Store OL overlays for cleanup
  const [clusteredData, setClusteredData] = useState([]); // Clustered standstill groups
  const zoomDebounceRef = useRef(null); // Debounce timer for zoom recalculation

  const location = useLocation();

  useEffect(() => {
    // Dismiss all toasts when the route changes
    toast.dismiss();
  }, [location, deviceId, selectedVehiclePlate]);

  useEffect(() => {
    return () => {
      toast.dismiss(); // clear toasts on unmount
    };
  }, []);

  const validTrackData = useMemo(() => {
    if (!trackData.length) return [];

    return removeDuplicates(
      trackData.filter(
        (point) =>
          point &&
          typeof point.lat === "number" &&
          typeof point.lng === "number" &&
          (point.hdop == null || point.hdop === "N/A" || Number(point.hdop) <= maxHdopThreshold)
      )
    );
  }, [trackData, maxHdopThreshold]);

  useEffect(() => {
    // Only reset showToast when page resets to 0 (new data load starting)
    if (page === 0) {
      setShowToast(false);
    }
  }, [page]);

  // Function to decimate points for better performance (Future use)
  const decimatePoints = (points, maxPoints) => {
    if (points.length <= maxPoints) return points;

    const result = [];
    const step = Math.floor(points.length / maxPoints);

    // Always include first and last points
    result.push(points[0]);

    // Add evenly spaced points
    for (let i = step; i < points.length - step; i += step) {
      result.push(points[i]);
    }

    // Add last point
    result.push(points[points.length - 1]);

    return result;
  };

  useEffect(() => {
    if (clientConfig?.tracking?.web?.map?.hdop_threshold) {
      setMaxHdopThreshold(clientConfig?.tracking?.web?.map?.hdop_threshold);
    }
  }, []);

  const isInitialMountRef = useRef(true);
  useEffect(() => {
    // Skip on initial mount — only trigger resetCount when device/vehicle CHANGES
    // after the component is already rendered. This prevents the date picker from
    // recalculating the range on tab switch, which causes duplicate API requests.
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    if (deviceId || selectedVehiclePlate) {
      setResetCount((c) => c + 1);
    }
  }, [deviceId, selectedVehiclePlate]);

  // Reset state and map layers when selected time range or device ID changes
  useEffect(() => {
    // Clean up all cluster/standstill overlays before resetting state

    // Clean up OL standstill overlays
    if (mapProvider === "qiMap" && trackMapRef && trackMapRef.getMapReference) {
      const olMap = trackMapRef.getMapReference();
      if (olMap) {
        standstillOverlaysRef.current.forEach((overlay) => {
          olMap.removeOverlay(overlay);
        });
      }
    }
    standstillOverlaysRef.current = [];

    // Clean up Mapbox standstill markers
    if (window.standstillMarkers) {
      window.standstillMarkers.forEach((m) => m.remove());
      window.standstillMarkers = [];
    }

    // Clean up Google Maps standstill markers (stored in window[track_${i}_google])
    if (mapProvider === "google") {
      for (let i = 0; i <= count; i++) {
        const previousTrack = window[`track_${i}_google`];
        if (previousTrack) {
          previousTrack.forEach((overlay) => overlay.setMap(null));
          delete window[`track_${i}_google`];
        }
      }
    }

    setPage(0);
    setTrackData([]);
    setResData(undefined); // Clear stale response data from previous vehicle/range
    setInitialLoad(true); // Set initial load to true when deviceId or range changes
    setNeedsRedraw(true);
    setIsLoading(true); // Reset loading state for new data fetch
    setNoDataAvailable(false); // Reset no-data flag
    setAllDataCollected(false); // Reset all-data-collected flag
    setShowToast(false); // Prevent stale toasts from previous load
    setHasSetInitialZoom(false); // Reset initial zoom flag for new device/vehicle
    setStandstillData([]); // Reset standstill data
    allPacketsRef.current = []; // Reset accumulated packets
    lastProcessedResDataRef.current = null; // Reset processed data tracker
    lastProcessedTrackResDataRef.current = null; // Reset track data processing tracker
    setSelectedStandstill(null);
    setShowStandstillPopup(false);
    setClusteredData([]); // Reset clustered data
    toast.dismiss(); // Dismiss any existing toasts from previous state
  }, [selectedRange, deviceId, selectedVehiclePlate]);

  const queryParams = useMemo(
    () => ({
      deviceId,
      per_page: totalCount,
      page,
      start_time: moment(selectedRange.startDate).valueOf(),
      end_time: moment(selectedRange.endDate).valueOf(),
      isTime: true,
      plate_number: selectedVehiclePlate,
      type: type,
      // The map paginates through the whole range and re-sorts polyline points by
      // timestamp, so it keeps ascending order (design "Route Loading Strategy" -
      // only the live summary loads the newest page first).
      sortOrder: "ASC",
    }),
    [
      deviceId,
      totalCount,
      page,
      selectedRange.startDate,
      selectedRange.endDate,
      selectedVehiclePlate,
      type,
    ]
  );

  const [resData, setResData] = useState(undefined);
  const [error, setError] = useState({});

  // Fetch vehicle routing data using a custom hook
  const { data: devicesResponse, error: devicesError, isFetching: devicesIsFetching } = useGetVehicleRoutingQuery(queryParams, {
    skip: type === "Vehicles" ? true : false,
  });

  //Fetch Track Points Data for Vehicles
  const { data: vehiclesResponse, error: vehiclesError, isFetching: vehiclesIsFetching } = useGetFormattedVehicleRouteQuery(
    queryParams,
    { skip: type === "Devices" ? true : false }
  );

  const isQueryFetching = type === "Vehicles" ? vehiclesIsFetching : devicesIsFetching;

  useEffect(() => {
    if (type === "Devices") {
      setResData(devicesResponse);
      setError(devicesError);
    } else if (type === "Vehicles") {
      setResData(vehiclesResponse);
      setError(vehiclesError);
    }
  }, [devicesResponse, devicesError, vehiclesResponse, vehiclesError]);

  // Accumulate all raw packets across pages for standstill calculation
  const allPacketsRef = useRef([]);
  const lastProcessedResDataRef = useRef(null);

  // Collect packets from each page and recalculate standstills
  useEffect(() => {
    if (resData && resData.data && resData.data.length > 0 && resData !== lastProcessedResDataRef.current) {
      lastProcessedResDataRef.current = resData;
      if (allPacketsRef.current.length === 0 || page <= 1) {
        // First page — reset accumulated packets
        allPacketsRef.current = [...resData.data];
      } else {
        // Subsequent pages — append
        allPacketsRef.current = [...allPacketsRef.current, ...resData.data];
      }

      // Get standstill config from clientConfig or use defaults
      const standstillConfig = {
        speedThreshold: clientConfig?.tracking?.web?.standstill?.speed_threshold || 5, // km/h
        durationThreshold: clientConfig?.tracking?.web?.standstill?.duration_threshold || 60, // seconds
      };

      // Recalculate standstills from all accumulated packets with config
      const calculatedStandstills = calculateStandstills(allPacketsRef.current, standstillConfig);

      // Enrich standstills with reverse geocoded addresses (fallback if address is missing)
      enrichStandstillsWithAddresses(calculatedStandstills).then((enrichedStandstills) => {
        setStandstillData(enrichedStandstills || []);
      });
    }
  }, [resData, clientConfig]);

  /**
   * Enriches standstill data with reverse geocoded addresses for entries without address
   * @param {Array} standstills - Array of standstill entries
   * @returns {Promise<Array>} Promise resolving to standstills with enriched addresses
   */
  const enrichStandstillsWithAddresses = async (standstills) => {
    if (!standstills || standstills.length === 0) {
      return standstills;
    }

    // Find standstills that need address enrichment (no address or all fields empty)
    const needsEnrichment = standstills.filter((standstill) => {
      if (!standstill.address) return true;

      const addr = standstill.address;
      const hasAnyAddress = addr.city || addr.region || addr.street || addr.name;
      return !hasAnyAddress;
    });

    if (needsEnrichment.length === 0) {
      return standstills;
    }

    // Fetch addresses for standstills that need enrichment
    const addressPromises = needsEnrichment.map(async (standstill) => {
      try {
        const authToken = sessionStorage.getItem("fmdp-access-token");
        const baseUrl = process.env.REACT_APP_REVERSE_GEO_CODING_API;

        if (!baseUrl) {
          console.warn('Reverse geocoding API URL not configured');
          return null;
        }

        const url = new URL(`${baseUrl}/reverse`);
        url.searchParams.append('format', 'json');
        url.searchParams.append('lat', standstill.position.lat);
        url.searchParams.append('lon', standstill.position.lng);

        const response = await fetch(url.toString(), {
          headers: authToken ? { 'Authorization': authToken } : {},
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data && data.address) {
          return {
            id: standstill.id,
            address: {
              name: data.address.name || '',
              street: data.address.road || data.address.street || '',
              city: data.address.city || data.address.town || data.address.village || '',
              region: data.address.state || data.address.region || '',
              country: data.address.country || '',
              postalCode: data.address.postcode || '',
            },
          };
        }
      } catch (error) {
        console.warn(`Failed to fetch address for standstill ${standstill.id}:`, error);
      }
      return null;
    });

    const fetchedAddresses = await Promise.all(addressPromises);

    // Merge fetched addresses back into standstills
    const enrichedStandstills = standstills.map((standstill) => {
      const fetchedAddress = fetchedAddresses.find((fa) => fa && fa.id === standstill.id);
      if (fetchedAddress) {
        return {
          ...standstill,
          address: fetchedAddress.address,
          // Update gps.position.address for compatibility with getDisplayAddress
          gps: {
            ...standstill.gps,
            position: {
              ...standstill.gps.position,
              address: fetchedAddress.address,
            },
          },
        };
      }
      return standstill;
    });

    return enrichedStandstills;
  };

  // Build toPixel function based on active map provider
  const getToPixel = useCallback(() => {
    if (mapProvider === "qiMap" && trackMapRef && trackMapRef.getMapReference) {
      const olMap = trackMapRef.getMapReference();
      if (!olMap) return null;
      return (lat, lng) => {
        const pixel = olMap.getPixelFromCoordinate(fromLonLat([lng, lat]));
        return pixel ? { x: pixel[0], y: pixel[1] } : null;
      };
    }
    if (mapProvider === "mapbox" && trackMapRef && trackMapRef.project) {
      return (lat, lng) => {
        const point = trackMapRef.project([lng, lat]);
        return { x: point.x, y: point.y };
      };
    }
    if (mapProvider === "google" && trackMapRef) {
      // For Google Maps, we need an OverlayView to get projection
      // Use a temporary overlay to access the projection
      return (lat, lng) => {
        const projection = trackMapRef.__standstillOverlay && trackMapRef.__standstillOverlay.getProjection();
        if (!projection) return null;
        const point = projection.fromLatLngToContainerPixel(new google.maps.LatLng(lat, lng));
        return point ? { x: point.x, y: point.y } : null;
      };
    }
    return null;
  }, [mapProvider, trackMapRef]);

  // Recalculate clustering when standstillData changes or zoom changes
  const recalculateClustering = useCallback(() => {
    if (!standstillData.length) {
      setClusteredData([]);
      return;
    }
    const toPixel = getToPixel();
    if (!toPixel) return;
    const clusters = clusterStandstills(standstillData, toPixel);
    setClusteredData(clusters);
  }, [standstillData, getToPixel]);

  // Recalculate clustering when standstillData changes
  useEffect(() => {
    recalculateClustering();
  }, [standstillData, recalculateClustering]);

  // Zoom change listener with debounce for recalculating clustering
  useEffect(() => {
    if (!trackMapRef || !standstillData.length) return;

    const debouncedRecalc = () => {
      if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
      zoomDebounceRef.current = setTimeout(() => {
        recalculateClustering();
      }, 180);
    };

    if (mapProvider === "qiMap" && trackMapRef.getMapReference) {
      const olMap = trackMapRef.getMapReference();
      if (!olMap) return;
      olMap.getView().on("change:resolution", debouncedRecalc);
      return () => {
        olMap.getView().un("change:resolution", debouncedRecalc);
        if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
      };
    }

    if (mapProvider === "mapbox" && trackMapRef.on) {
      trackMapRef.on("zoom", debouncedRecalc);
      return () => {
        trackMapRef.off("zoom", debouncedRecalc);
        if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
      };
    }

    if (mapProvider === "google" && trackMapRef.addListener) {
      const listener = trackMapRef.addListener("zoom_changed", debouncedRecalc);
      return () => {
        google.maps.event.removeListener(listener);
        if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
      };
    }

    return () => {
      if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
    };
  }, [mapProvider, trackMapRef, standstillData, recalculateClustering]);

  // Error message display
  useEffect(() => {
    if (error?.data?.message) {
      toast.error(error?.data?.message);
    }
  }, [error]);

  // Add new effect to check for last known location from trackingPoints
  useEffect(() => {
    if (trackingPoints && (deviceId || selectedVehiclePlate)) {
      setLastKnownLocation(null);
      let lastLocation;
      if (type !== "Vehicles") {
        lastLocation = trackingPoints.find((device_data) => device_data.imei === deviceId);
      } else {
        lastLocation = trackingPoints.find(
          (device_data) => device_data.plate_number === selectedVehiclePlate
        );
      }

      if (lastLocation?.lat && lastLocation?.lng) {
        const newLastLocation = {
          lat: lastLocation?.lat,
          lng: lastLocation?.lng,
        };
        setLastKnownLocation(newLastLocation);
      }
    }
  }, [trackingPoints, deviceId, selectedVehiclePlate, type]);

  // Modify the data processing effect to handle no data and invalid data cases
  useEffect(() => {
    try {
      // Guard: don't process if resData is the initial empty array or hasn't been populated yet
      if (!resData || (Array.isArray(resData) && resData.length === 0) || !resData?.data) {
        return;
      }

      // Guard: don't process stale data while a new query is already in flight
      if (isQueryFetching) {
        return;
      }

      // Guard: don't re-process the same resData reference (prevents processing stale data
      // when deviceId/selectedVehiclePlate changes but resData hasn't updated yet)
      if (resData === lastProcessedTrackResDataRef.current) {
        return;
      }
      lastProcessedTrackResDataRef.current = resData;

      if (
        (selectedVehiclePlate || deviceId) &&
        resData?.total_count &&
        page <= Math.round(resData?.total_count / totalCount)
      ) {
        const newPoints = [];

        resData?.data.forEach((device_data) => {
          if (
            device_data?.gps &&
            device_data?.gps?.position?.lat &&
            device_data?.gps?.position?.lng &&
            (!device_data?.gps?.hdop || device_data?.gps?.hdop <= maxHdopThreshold)
          ) {
            newPoints.push({
              source_id: device_data?.source_id,
              lat: device_data?.gps?.position?.lat,
              lng: device_data?.gps?.position?.lng,
              direction: device_data?.velocity?.direction || "N/A",
              speed: device_data?.velocity?.speed || 0,
              "010d_vehicle_speed": device_data?.obd?.["010d_vehicle_speed"] || "N/A",
              sourceTime: device_data?.source_time,
              timestamp: moment(device_data?.source_time).valueOf(),
              hdop: device_data?.gps?.hdop ? Number(device_data?.gps?.hdop).toFixed(2) : "N/A",
            });
          }
        });

        if (newPoints.length > 0) {
          setTrackData((prevData) => {
            const combined = [...prevData, ...newPoints];
            combined.sort((a, b) => a.timestamp - b.timestamp);
            const uniquePoints = [];
            const seen = new Set();

            for (const point of combined) {
              const key = `${point.lat.toFixed(5)},${point.lng.toFixed(5)}`;
              if (!seen.has(key)) {
                seen.add(key);
                uniquePoints.push(point);
              }
            }

            return uniquePoints;
          });

          setNeedsRedraw(true);
          setPage((c) => c + 1);
          setIsLoading(false);
          setNoDataAvailable(false);
        } else {
          if (page === 0) {
            setNoDataAvailable(true);
            setIsLoading(false);
            setShowToast(true);
          } else {
            // Non-first page returned no valid GPS points but we may still have more pages
            setPage((c) => c + 1);
          }
        }
      } else if (resData?.total_count === 0) {
        // API returned a valid response with zero total count - genuinely no data
        setNoDataAvailable(true);
        setAllDataCollected(true);
        setIsLoading(false);
        setShowToast(true);
      } else {
        // Either all pages have been fetched (page > total/perPage),
        // or current page returned empty data array (no more items)
        setAllDataCollected(true);
        setIsLoading(false);
        setShowToast(true);
      }
    } catch (e) {
      console.log(e);
      setIsLoading(false);
      setAllDataCollected(true);
    }
  }, [resData, deviceId, maxHdopThreshold, selectedVehiclePlate, isQueryFetching]);

  // Add effect to show toast messages when all data is collected
  useEffect(() => {
    // Only show toast when loading is complete, query has settled, and we have a definitive answer
    if (!isLoading && !isQueryFetching && (allDataCollected || noDataAvailable)) {
      if (showToast) {
        if (validTrackData.length === 0 && trackData.length !== 0) {
          setShowToast(false); // Reset showToast to prevent repeated toasts
          toast.info("No valid data for the selected time window. Showing last known location.", {
            autoClose: false,
            closeButton: true,
            className: "custom-toast",
          });
        } else if (trackData.length === 0) {
          setShowToast(false); // Reset showToast to prevent repeated toasts
          toast.info(
            "No data available for the selected time window. Showing last known location.",
            {
              autoClose: false,
              closeButton: true,
              className: "custom-toast",
            }
          );
        } else {
          setShowToast(false); // Reset showToast to prevent repeated toasts
        }
      }
    }
  }, [
    allDataCollected,
    isLoading,
    isQueryFetching,
    validTrackData.length,
    trackData.length,
    noDataAvailable,
    showToast,
  ]);

  // Handle real-time data updates separately to avoid frequent state updates
  useEffect(() => {
    try {
      // Check if real-time data is within time range and has valid GPS with acceptable HDOP
      if (
        !realTimeData?.gps ||
        !realTimeData?.gps?.position?.is_valid ||
        (realTimeData?.gps?.hdop != null && realTimeData?.gps?.hdop > maxHdopThreshold)
      ) {
        // Clear any existing timeout
        if (liveDataTimeoutRef.current) {
          clearTimeout(liveDataTimeoutRef.current);
        }
        // Set a 1.5 second timeout before setting isLiveData to false
        liveDataTimeoutRef.current = setTimeout(() => {
          setIsLiveData(false);
        }, 1500);
        return;
      }

      const currentTime = moment();
      const dataTime = moment(realTimeData?.source_time);
      const timeDiff = currentTime.diff(dataTime, "minutes");

      const selectedTime = moment(selectedRange.startDate).format("YYYY-MM-DD HH:mm:ss");
      const duration = moment.duration(moment(currentTime).diff(moment(selectedTime)));
      const hours = duration.asHours();

      if (hours <= 25) {
        // Clear any existing timeout since we got new data
        if (liveDataTimeoutRef.current) {
          clearTimeout(liveDataTimeoutRef.current);
        }
        setIsLiveData(true); // Set flag to indicate live data is being received

        const newPoint = {
          source_id: realTimeData?.source_id,
          lat: realTimeData?.gps?.position?.lat,
          lng: realTimeData?.gps?.position?.lng,
          direction: realTimeData?.velocity?.direction || "N/A",
          speed: realTimeData?.velocity?.speed || 0,
          "010d_vehicle_speed": realTimeData?.obd?.["010d_vehicle_speed"] || "N/A",
          sourceTime: realTimeData?.source_time,
          timestamp: moment(realTimeData?.source_time || new Date()).valueOf(),
          hdop: realTimeData?.gps?.hdop ? Number(realTimeData?.gps?.hdop).toFixed(2) : "N/A",
        };

        // Only update if the point is actually new
        // setTrackData((prevData) => {
        /* const exists = prevData.some(
            (point) =>
              Math.abs(point.lat - newPoint.lat) < 0.00001 &&
              Math.abs(point.lng - newPoint.lng) < 0.00001
          ); */

        //console.log(exists);

        //if (!exists) {
        // const updatedData = [...prevData, newPoint];
        //updatedData.sort((a, b) => a.timestamp - b.timestamp);
        // setNeedsRedraw(true);
        // return updatedData;
        //}

        //return prevData;
        //});
      }
    } catch (e) {
      console.log(e);
    }
  }, [, /* realTimeData */ selectedRange, maxHdopThreshold]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (liveDataTimeoutRef.current) {
        clearTimeout(liveDataTimeoutRef.current);
      }
      if (zoomDebounceRef.current) {
        clearTimeout(zoomDebounceRef.current);
      }
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }

      // Clean up OL standstill overlays
      standstillOverlaysRef.current.forEach((overlay) => {
        try {
          const el = overlay.getElement();
          if (el && el.parentElement) el.parentElement.removeChild(el);
        } catch (e) { /* ignore */ }
      });
      standstillOverlaysRef.current = [];

      // Clean up Mapbox standstill markers
      if (window.standstillMarkers) {
        window.standstillMarkers.forEach((m) => {
          try { m.remove(); } catch (e) { /* ignore */ }
        });
        window.standstillMarkers = [];
      }
    };
  }, []);

  // Attach event listeners to detect user interaction (qiMap only)
  useEffect(() => {
    if (mapProvider === "qiMap" && trackMapRef && trackMapRef.getMapReference) {
      const olMap = trackMapRef.getMapReference();
      if (!olMap || !olMap.on) return;

      const onInteractionStart = () => {
        setUserInteracting(true);
        if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
      };
      const onInteractionEnd = () => {
        if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
        // Delay before setting userInteracting to false (e.g., 2 seconds)
        interactionTimeoutRef.current = setTimeout(() => setUserInteracting(false), 0);
      };

      olMap.on("movestart", onInteractionStart);
      olMap.on("moveend", onInteractionEnd);
      olMap.on("pointerdrag", onInteractionStart);
      olMap.on("change:resolution", onInteractionStart);

      return () => {
        olMap.un("movestart", onInteractionStart);
        olMap.un("moveend", onInteractionEnd);
        olMap.un("pointerdrag", onInteractionStart);
        olMap.un("change:resolution", onInteractionStart);
        if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
      };
    }
  }, [mapProvider, trackMapRef]);

  // When userInteracting becomes false and a fit is pending, perform the fit
  useEffect(() => {
    if (mapProvider === "qiMap" && trackMapRef && pendingFitLayer && !userInteracting) {
      try {
        // Only perform fit if it's initial load or device/plate changed
        if (initialLoad) {
          trackMapRef.performFit(pendingFitLayer);
          setInitialLoad(false); // Reset initial load after first fit
        }
      } catch (e) {
        console.warn(`Failed to perform fit on layer ${pendingFitLayer}:`, e);
      }
      setPendingFitLayer(null);
    }
  }, [userInteracting, pendingFitLayer, mapProvider, trackMapRef, initialLoad]);

  //Map Loading State
  useEffect(() => {
    if (deviceId || selectedVehiclePlate) {
      setIsLoading(true);
      setInitialLoad(true); // Set initial load to true when device/plate changes
      setAllDataCollected(false); // Reset data collection state
      setNoDataAvailable(false); // Reset no data available state
    }
  }, [deviceId, selectedVehiclePlate]);

  // Update the map with the tracked vehicle data
  useEffect(() => {
    if (mapProvider === "qiMap") {
      try {
        if (trackMapRef && validTrackData.length && (deviceId || selectedVehiclePlate)) {
          // Remove previous layer if it exists (by name) and add a new one
          let layerName = `track_${count}`;

          trackMapRef.removeLayer(`track_${count - 1}`);
          trackMapRef.addLayer({ name: layerName });

          // Draw the first and last points of the track with specific styles
          trackMapRef.drawTrack({
            layerName: layerName,
            fitWithinView: false, // Disable auto-fit for updates
            trackType: "point",
            data: [{ coordinates: [validTrackData[0]] }],
            style: {
              point: {
                style: (feature) => {
                  return new Style({
                    image: new Icon({
                      crossOrigin: "anonymous",
                      src: homeLocation,
                      imgSize: [34, 40],
                      anchor: [0.5, 0.9],
                    }),
                  });
                },
              },
            },
          });

          // Always draw end marker, but use different icon based on live status
          const lastPointSrc = /* isLiveData ? pointerNew :  */ endPointMarkerNew;
          const lastPoint = validTrackData[validTrackData.length - 1];

          // Ensure end marker is always drawn
          if (lastPoint) {
            trackMapRef.drawTrack({
              layerName: layerName,
              fitWithinView: false, // Disable auto-fit for updates
              trackType: "point",
              data: [{ coordinates: [lastPoint] }],
              style: {
                point: {
                  style: (feature) => {
                    return new Style({
                      image: new Icon({
                        crossOrigin: "anonymous",
                        src: destination,
                        imgSize: [34, 40],
                        rotation: 0,
                        anchor: [0.5, 0.9],
                      }),
                    });
                  },
                },
              },
            });
          }

          // Draw the track line connecting all points
          trackMapRef.drawTrack({
            layerName: layerName,
            fitWithinView: false, // Disable auto-fit for updates
            trackType: "line",
            data: [{ coordinates: validTrackData }],
            style: {
              line: {
                style: (feature) => {
                  return new Style({
                    stroke: new Stroke({
                      color: "#0767d0",
                      width: 5,
                    }),
                  });
                },
              },
            },
            /* tooltip: showTooltip && {
              showOn: "click",
              content: (feature) => {
                let { coordinate } = feature.get("data");
                return `
                  <ul>
                    <li><strong>Latitude: </strong> <span>${Number(coordinate?.lat || 0).toFixed(
                      5
                    )}</span></li>
                    <li><strong>Longitude: </strong> <span>${Number(coordinate?.lng || 0).toFixed(
                      5
                    )}</span></li>
                    <li><strong>Direction: </strong> <span>${
                      coordinate?.direction || "N/A"
                    }</span></li>
                    <li><strong>Speed: </strong> <span>${
                      coordinate?.speed ? Number(coordinate?.speed * 3.6).toFixed(1) : "0.0"
                    } km/h</span></li>
                    <li><strong>HDOP: </strong> <span>${
                      typeof coordinate?.hdop === "number"
                        ? Number(coordinate?.hdop).toFixed(2)
                        : coordinate?.hdop || "N/A"
                    }</span></li>
                    <li><strong>Source Time: </strong> <span>${
                      coordinate?.sourceTime
                        ? moment(coordinate?.sourceTime).format("YYYY-MM-DD HH:mm:ss")
                        : "N/A"
                    }</span></li>
                  </ul>
                `;
              },
            }, */
          });

          // Draw directional arrows along the track line
          const arrowPoints = generateDirectionalArrowPoints(validTrackData, 500);
          if (arrowPoints.length > 0) {
            trackMapRef.drawTrack({
              layerName: layerName,
              fitWithinView: false,
              trackType: "point",
              data: [{ coordinates: arrowPoints }],
              style: {
                point: {
                  style: (feature) => {
                    const dir = feature.direction || feature.get?.("data")?.direction || 0;
                    return new Style({
                      zIndex: 2,
                      image: new Icon({
                        src: directionArrow,
                        scale: 0.6,
                        rotation: getRotation(dir + 90),
                        anchor: [0.5, 0.5],
                      }),
                    });
                  },
                },
              },
            });
          }

          // Draw mapPoint if it exists and is valid
          if (mapPoint && mapPoint.lat && mapPoint.lng && mapPoint.is_valid) {
            trackMapRef.drawTrack({
              layerName: layerName,
              fitWithinView: false,
              trackType: "point",
              data: [{ coordinates: [mapPoint] }],
              style: {
                point: {
                  style: (feature) => {
                    return new Style({
                      zIndex: 9999, // Ensure selected vehicle is on top
                      image: new Icon({
                        crossOrigin: "anonymous",
                        src: data_point,
                        imgSize: [34, 40],
                        rotation: getRotation(feature.direction),
                      }),
                    });
                  },
                },
              },
            });

            // Center on mapPoint with initial zoom of 19, then preserve user's zoom
            try {
              const olMap = trackMapRef.getMapReference();
              if (olMap) {
                // Transform coordinates from WGS84 to map projection
                const view = olMap.getView();
                const mapPointCoords = fromLonLat([mapPoint.lng, mapPoint.lat]);

                // Center on mapPoint
                view.setCenter(mapPointCoords);

                // Set zoom to 19 only on first mapPoint selection
                if (!hasSetInitialZoom) {
                  view.setZoom(19);
                  setHasSetInitialZoom(true);
                }
              }
            } catch (e) {
              console.warn("Failed to center map on mapPoint:", e);
            }
          }

          // Reset initial zoom flag when mapPoint is deselected
          if (!mapPoint && hasSetInitialZoom) {
            setHasSetInitialZoom(false);
          }

          // Only fit the map on initial load or device/plate changes
          if (!userInteracting && initialLoad) {
            try {
              trackMapRef.performFit(layerName); // Fit to the layer
              setInitialLoad(false); // Disable initial load after first fit
            } catch (e) {
              console.warn(`Failed to perform fit on layer ${layerName}:`, e);
            }
          } else if (userInteracting && initialLoad) {
            setPendingFitLayer(layerName); // Remember to fit this layer later when user stops interacting
          }

          setCount((c) => c + 1);
        } else if (lastKnownLocation) {
          // Draw only the last known location
          // Clean up ALL previous layers first
          for (let i = 0; i <= count; i++) {
            trackMapRef.removeLayer(`track_${i}`);
          }

          let layerName = `track_${count}`;
          trackMapRef.addLayer({ name: layerName });

          trackMapRef.drawTrack({
            layerName: layerName,
            fitWithinView: true,
            trackType: "point",
            data: [{ coordinates: [lastKnownLocation] }],
            style: {
              point: {
                style: (feature) => {
                  return new Style({
                    image: new Icon({
                      crossOrigin: "anonymous",
                      src: direction,
                      imgSize: [34, 40],
                      rotation:
                        lastKnownLocation.direction !== "N/A" ? lastKnownLocation.direction : 0,
                    }),
                  });
                },
              },
            },
          });

          // Increment count after successful draw
          setCount((c) => c + 1);
        } else {
          // Clean up ALL layers when no data to show
          for (let i = 0; i <= count; i++) {
            trackMapRef?.removeLayer(`track_${i}`);
          }
          // Clean up standstill overlays
          const olMap = trackMapRef?.getMapReference?.();
          if (olMap) {
            standstillOverlaysRef.current.forEach((overlay) => {
              olMap.removeOverlay(overlay);
            });
            standstillOverlaysRef.current = [];
          }
        }
      } catch (e) {
        console.log(e);
      }
    }
    if (mapProvider === "mapbox") {
      try {
        // Maintain references to the layers and markers
        let currentLayerIds = [];

        // Helper function to remove a layer and its source
        const removeLayerAndSource = (id) => {
          if (trackMapRef.getLayer(id)) {
            trackMapRef.removeLayer(id);
          }
          if (trackMapRef.getSource(id)) {
            trackMapRef.removeSource(id);
          }
        };

        // Function to clear all previous layers and sources
        const clearPreviousLayers = () => {
          currentLayerIds.forEach((layerId) => {
            removeLayerAndSource(layerId);
          });
          currentLayerIds = [];
        };

        // Function to clear previous markers
        const clearPreviousMarkers = () => {
          if (window.startMarker) {
            window.startMarker.remove();
            window.startMarker = null;
          }
          if (window.endMarker) {
            window.endMarker.remove();
            window.endMarker = null;
          }
          if (window.mapPointMarker) {
            window.mapPointMarker.remove();
            window.mapPointMarker = null;
          }
        };

        if (trackMapRef && validTrackData.length && (deviceId || selectedVehiclePlate)) {
          const layerName = `track_${count}`;

          // Clear previous layers and markers
          clearPreviousLayers();
          clearPreviousMarkers();

          // Add track line as a source
          trackMapRef.addSource(`${layerName}_line`, {
            type: "geojson",
            data: {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: validTrackData.map((point) => [point.lng, point.lat]),
              },
            },
          });

          trackMapRef.addLayer({
            id: `${layerName}_line`,
            type: "line",
            source: `${layerName}_line`,
            paint: {
              "line-color": "#000", // Black line
              "line-width": 3,
            },
          });

          // Track current layer ID for future cleanup
          currentLayerIds.push(`${layerName}_line`);

          // Fit once for a newly selected vehicle/range. Subsequent redraws must
          // keep the viewport chosen by the user.
          const bounds = validTrackData.reduce(
            (bounds, coord) => bounds.extend([coord.lng, coord.lat]),
            new mapboxgl.LngLatBounds(
              [validTrackData[0].lng, validTrackData[0].lat],
              [validTrackData[0].lng, validTrackData[0].lat]
            )
          );

          if (initialLoad && !mapPoint) {
            trackMapRef.fitBounds(bounds, { padding: 20 });
            setInitialLoad(false);
          }

          // Add start marker
          window.startMarker = new mapboxgl.Marker({
            element: createCustomMarkerElement(homeLocation),
          })
            .setLngLat([validTrackData[0].lng, validTrackData[0].lat])
            .addTo(trackMapRef);

          // Always create end marker with custom icon
          const endMarker = new mapboxgl.Marker({
            element: createCustomMarkerElement(destination),
          })
            .setLngLat([
              validTrackData[validTrackData.length - 1].lng,
              validTrackData[validTrackData.length - 1].lat,
            ])
            .addTo(trackMapRef);

          // Add mapPoint marker if it exists and is valid
          if (mapPoint && mapPoint.lat && mapPoint.lng && mapPoint.is_valid) {
            window.mapPointMarker = new mapboxgl.Marker({
              element: createCustomMarkerElement(locationImgNew),
            })
              .setLngLat([mapPoint.lng, mapPoint.lat])
              .addTo(trackMapRef);

            // Center on mapPoint with initial zoom of 19, then preserve user's zoom
            try {
              const targetZoom = !hasSetInitialZoom ? 19 : trackMapRef.getZoom();
              trackMapRef.flyTo({
                center: [mapPoint.lng, mapPoint.lat],
                zoom: targetZoom, // Use zoom 19 on first selection, preserve current zoom afterwards
                speed: 1.2,
                curve: 1,
                essential: true,
              });

              // Mark that initial zoom has been set
              if (!hasSetInitialZoom) {
                setHasSetInitialZoom(true);
              }
            } catch (e) {
              console.warn("Failed to center map on mapPoint:", e);
            }
          }

          // Reset initial zoom flag when mapPoint is deselected
          if (!mapPoint && hasSetInitialZoom) {
            setHasSetInitialZoom(false);
          }

          // Increment the count for the next layer
          setCount((c) => c + 1);
        } else {
          // Clear all layers and markers if no valid data
          clearPreviousLayers();
          clearPreviousMarkers();

          // Clear standstill markers
          if (window.standstillMarkers) {
            window.standstillMarkers.forEach(marker => marker.remove());
            window.standstillMarkers = [];
          }
        }
      } catch (e) {
        console.error("Error:", e);
      }
    }
    if (mapProvider === "google") {
      try {
        if (trackMapRef && validTrackData.length && (deviceId || selectedVehiclePlate)) {
          let layerName = `track_${count}`;

          // Remove previous tracks (if they exist)
          for (let i = 0; i < count; i++) {
            const previousTrack = window[`track_${i}_google`];
            if (previousTrack) {
              previousTrack.forEach((overlay) => overlay.setMap(null));
            }
          }

          // Create start marker with custom icon
          const startMarker = new google.maps.Marker({
            position: { lat: validTrackData[0].lat, lng: validTrackData[0].lng },
            map: trackMapRef,
            icon: {
              url: homeLocation,
              scaledSize: new google.maps.Size(38, 38),
            },
          });

          // Always create end marker with custom icon
          const endMarker = new google.maps.Marker({
            position: {
              lat: validTrackData[validTrackData.length - 1].lat,
              lng: validTrackData[validTrackData.length - 1].lng,
            },
            map: trackMapRef,
            icon: {
              url: destination,
              scaledSize: new google.maps.Size(38, 38),
            },
          });

          // Create a polyline using the default Google Maps polyline
          const trackLine = new google.maps.Polyline({
            path: validTrackData.map((point) => ({
              lat: point.lat,
              lng: point.lng,
            })),
            geodesic: true,
            strokeColor: "#0767d0",
            strokeOpacity: 1.0,
            strokeWeight: 5,
            icons: [
              {
                icon: {
                  path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  scale: 2.5,
                  strokeColor: "#ffffff",
                  strokeOpacity: 0.8,
                  strokeWeight: 1,
                  fillColor: "#ffffff",
                  fillOpacity: 0.6,
                },
                offset: "0",
                repeat: "100px",
              },
            ],
          });

          trackLine.setMap(trackMapRef);

          // Add mapPoint marker if it exists and is valid
          let mapPointMarker = null;
          if (mapPoint && mapPoint.lat && mapPoint.lng && mapPoint.is_valid) {
            mapPointMarker = new google.maps.Marker({
              position: { lat: mapPoint.lat, lng: mapPoint.lng },
              map: trackMapRef,
              icon: {
                url: locationImgNew,
                scaledSize: new google.maps.Size(32, 32),
              },
            });

            // Center on mapPoint with initial zoom of 19, then preserve user's zoom
            try {
              trackMapRef.panTo({ lat: mapPoint.lat, lng: mapPoint.lng });

              // Set zoom to 19 only on first mapPoint selection
              if (!hasSetInitialZoom) {
                trackMapRef.setZoom(19);
                setHasSetInitialZoom(true);
              }
            } catch (e) {
              console.warn("Failed to center map on mapPoint:", e);
            }
          }

          // Reset initial zoom flag when mapPoint is deselected
          if (!mapPoint && hasSetInitialZoom) {
            setHasSetInitialZoom(false);
          }

          // Store the current track (include mapPointMarker if it exists)
          const trackElements = [startMarker, endMarker, trackLine];
          if (mapPointMarker) trackElements.push(mapPointMarker);
          window[`track_${count}_google`] = trackElements;

          // Auto-fit only for a newly selected vehicle/range. Live data redraws
          // should not override a manual zoom or pan.
          if (initialLoad && !mapPoint) {
            const bounds = new google.maps.LatLngBounds();
            validTrackData.forEach((point) =>
              bounds.extend(new google.maps.LatLng(point.lat, point.lng))
            );
            trackMapRef.fitBounds(bounds);
            setInitialLoad(false);
          }

          // Increment the count for the next layer
          setCount((c) => c + 1);
        } else {
          // Clear all previous Google Maps markers and lines
          for (let i = 0; i < count; i++) {
            const previousTrack = window[`track_${i}_google`];
            if (previousTrack) {
              previousTrack.forEach((overlay) => overlay.setMap(null));
            }
          }
        }
      } catch (e) {
        console.log(e);
      }
    }
  }, [
    trackMapRef,
    validTrackData,
    deviceId,
    initialLoad,
    mapProvider,
    maxHdopThreshold,
    isLiveData,
    selectedVehiclePlate,
    lastKnownLocation,
    mapPoint,
    hasSetInitialZoom,
  ]);

  // Separate useEffect for standstill/cluster marker rendering
  // Decoupled from track drawing to prevent zoom reset when clustering recalculates on zoom change
  useEffect(() => {
    if (!trackMapRef || !clusteredData.length) {
      // Clean up existing overlays if no data
      if (mapProvider === "qiMap") {
        const olMap = trackMapRef?.getMapReference?.();
        if (olMap) {
          standstillOverlaysRef.current.forEach((overlay) => {
            olMap.removeOverlay(overlay);
          });
          standstillOverlaysRef.current = [];
        }
      }
      if (mapProvider === "mapbox") {
        if (window.standstillMarkers) {
          window.standstillMarkers.forEach((m) => {
            try { m.remove(); } catch (e) { /* ignore */ }
          });
          window.standstillMarkers = [];
        }
      }
      return;
    }

    if (mapProvider === "qiMap") {
      const olMap = trackMapRef.getMapReference();
      if (olMap) {
        // Clean up previous standstill overlays
        standstillOverlaysRef.current.forEach((overlay) => {
          olMap.removeOverlay(overlay);
        });
        standstillOverlaysRef.current = [];

        // Close handler for all popovers
        const closeAllPopovers = (e) => {
          // Check if click is inside any open popover — if so, don't close
          if (e && e.target) {
            for (let i = 0; i < standstillOverlaysRef.current.length; i++) {
              const el = standstillOverlaysRef.current[i].getElement();
              if (el && el.contains(e.target)) return;
            }
          }
          standstillOverlaysRef.current.forEach((ov) => {
            const el = ov.getElement();
            if (el && el._closePopover) el._closePopover();
          });
        };

        olMap.getViewport().addEventListener("click", closeAllPopovers, true);

        clusteredData.forEach((group) => {
          if (group.count > 1) {
            // Render cluster marker using reusable dialog
            const vehicleReg = selectedVehiclePlate || deviceId || "Unknown";
            const { container: containerEl, open: openPopover, close: closePopover } = createClusterDialog(group, vehicleReg);

            const pill = containerEl.querySelector(".qi-map-info-dialog_pill");
            if (pill) {
              pill.addEventListener("click", (e) => {
                e.stopPropagation();
                closeAllPopovers();
                openPopover();
                try {
                  olMap.getView().animate({ center: fromLonLat([group.centroid.lng, group.centroid.lat]), duration: 300 });
                } catch (err) { /* ignore */ }
              });
            }

            const coords = fromLonLat([group.centroid.lng, group.centroid.lat]);
            const overlay = new Overlay({
              element: containerEl,
              position: coords,
              positioning: "bottom-center",
              stopEvent: false,
              offset: [0, 0],
            });

            olMap.addOverlay(overlay);
            standstillOverlaysRef.current.push(overlay);
          } else {
            // Render single standstill marker using reusable dialog
            const standstill = group.items[0];
            const vehicleReg = selectedVehiclePlate || deviceId || "Unknown";
            const { container: containerEl, open: openPopover, close: closePopover } = createStandstillPopupElement(standstill, vehicleReg);

            const pill = containerEl.querySelector(".qi-map-info-dialog_pill");
            if (pill) {
              pill.addEventListener("click", (e) => {
                e.stopPropagation();
                closeAllPopovers();
                openPopover();
                try {
                  olMap.getView().animate({ center: fromLonLat([standstill.position.lng, standstill.position.lat]), duration: 300 });
                } catch (err) { /* ignore */ }
              });
            }

            const coords = fromLonLat([standstill.position.lng, standstill.position.lat]);
            const overlay = new Overlay({
              element: containerEl,
              position: coords,
              positioning: "bottom-center",
              stopEvent: false,
              offset: [0, 0],
            });

            olMap.addOverlay(overlay);
            standstillOverlaysRef.current.push(overlay);
          }
        });
      }
    }

    if (mapProvider === "mapbox") {
      // Clean up previous standstill markers
      if (window.standstillMarkers) {
        window.standstillMarkers.forEach(m => m.remove());
        window.standstillMarkers = [];
      }

      // Close all popovers helper
      const closeAllMapboxPopovers = (e) => {
        // Check if click is inside any open popover — if so, don't close
        const target = e && (e.originalEvent ? e.originalEvent.target : e.target);
        if (target) {
          if (window.standstillMarkers) {
            for (let i = 0; i < window.standstillMarkers.length; i++) {
              const el = window.standstillMarkers[i].getElement();
              if (el && el.contains(target)) return;
            }
          }
        }
        if (window.standstillMarkers) {
          window.standstillMarkers.forEach(m => {
            const el = m.getElement();
            if (el && el._closePopover) el._closePopover();
          });
        }
      };

      clusteredData.forEach((group) => {
        if (group.count > 1) {
          // Render cluster marker using reusable dialog
          const vehicleReg = selectedVehiclePlate || deviceId || "Unknown";
          const { container: containerEl, open: openPopover, close: closePopover } = createClusterDialog(group, vehicleReg);

          const pill = containerEl.querySelector(".qi-map-info-dialog_pill");
          if (pill) {
            pill.addEventListener("click", (e) => {
              e.stopPropagation();
              closeAllMapboxPopovers();
              openPopover();
              try {
                trackMapRef.panTo([group.centroid.lng, group.centroid.lat]);
              } catch (err) { /* ignore */ }
            });
          }

          const marker = new mapboxgl.Marker({ element: containerEl, anchor: "bottom" })
            .setLngLat([group.centroid.lng, group.centroid.lat])
            .addTo(trackMapRef);

          if (!window.standstillMarkers) {
            window.standstillMarkers = [];
          }
          window.standstillMarkers.push(marker);
        } else {
          // Render single standstill marker using reusable dialog
          const standstill = group.items[0];
          const vehicleReg = selectedVehiclePlate || deviceId || "Unknown";
          const { container: containerEl, open: openPopover, close: closePopover } = createStandstillPopupElement(standstill, vehicleReg);

          const pill = containerEl.querySelector(".qi-map-info-dialog_pill");
          if (pill) {
            pill.addEventListener("click", (e) => {
              e.stopPropagation();
              closeAllMapboxPopovers();
              openPopover();
              try {
                trackMapRef.panTo([standstill.position.lng, standstill.position.lat]);
              } catch (err) { /* ignore */ }
            });
          }

          const marker = new mapboxgl.Marker({ element: containerEl, anchor: "bottom" })
            .setLngLat([standstill.position.lng, standstill.position.lat])
            .addTo(trackMapRef);

          if (!window.standstillMarkers) {
            window.standstillMarkers = [];
          }
          window.standstillMarkers.push(marker);
        }
      });

      // Close popovers on map click
      trackMapRef.on("click", closeAllMapboxPopovers);
    }

    if (mapProvider === "google") {
      // Clean up previous Google standstill overlays
      const prevTrackKey = Object.keys(window).find(k => k.match(/^track_\d+_google_standstills$/));
      if (window._googleStandstillOverlays) {
        window._googleStandstillOverlays.forEach((overlay) => overlay.setMap(null));
        window._googleStandstillOverlays = [];
      }

      // Custom overlay anchored at bottom-center
      class StandstillOverlay extends google.maps.OverlayView {
        constructor(position, element) {
          super();
          this.position = position;
          this.element = element;
        }

        onAdd() {
          this.getPanes().overlayMouseTarget.appendChild(this.element);
        }

        draw() {
          const projection = this.getProjection();
          const point = projection.fromLatLngToDivPixel(this.position);
          if (point) {
            this.element.style.left = point.x + "px";
            this.element.style.top = point.y + "px";
            this.element.style.transform = "translate(-50%, -100%)";
          }
        }

        onRemove() {
          if (this.element.parentElement) {
            this.element.parentElement.removeChild(this.element);
          }
        }
      }

      const standstillMarkers = [];

      clusteredData.forEach((group) => {
        if (group.count > 1) {
          // Render cluster marker using reusable dialog
          const vehicleReg = selectedVehiclePlate || deviceId || "Unknown";
          const { container: containerDiv, open: openPopover, close: closePopover } = createClusterDialog(group, vehicleReg);
          containerDiv.style.position = "absolute";

          const pill = containerDiv.querySelector(".qi-map-info-dialog_pill");
          if (pill) {
            pill.addEventListener("click", (e) => {
              e.stopPropagation();
              standstillMarkers.forEach((ov) => {
                const el = ov.element || ov.getElement?.();
                if (el && el._closePopover) el._closePopover();
              });
              openPopover();
              try {
                trackMapRef.panTo({ lat: group.centroid.lat, lng: group.centroid.lng });
              } catch (err) { /* ignore */ }
            });
          }

          const overlay = new StandstillOverlay(
            new google.maps.LatLng(group.centroid.lat, group.centroid.lng),
            containerDiv
          );
          overlay.element = containerDiv;

          overlay.setMap(trackMapRef);
          standstillMarkers.push(overlay);
        } else {
          // Render single standstill marker using reusable dialog
          const standstill = group.items[0];
          const vehicleReg = selectedVehiclePlate || deviceId || "Unknown";
          const { container: containerDiv, open: openPopover, close: closePopover } = createStandstillPopupElement(standstill, vehicleReg);
          containerDiv.style.position = "absolute";

          const pill = containerDiv.querySelector(".qi-map-info-dialog_pill");
          if (pill) {
            pill.addEventListener("click", (e) => {
              e.stopPropagation();
              standstillMarkers.forEach((ov) => {
                const el = ov.element || ov.getElement?.();
                if (el && el._closePopover) el._closePopover();
              });
              openPopover();
              try {
                trackMapRef.panTo({ lat: standstill.position.lat, lng: standstill.position.lng });
              } catch (err) { /* ignore */ }
            });
          }

          const overlay = new StandstillOverlay(
            new google.maps.LatLng(standstill.position.lat, standstill.position.lng),
            containerDiv
          );
          overlay.element = containerDiv;

          overlay.setMap(trackMapRef);
          standstillMarkers.push(overlay);
        }
      });

      // Store for cleanup
      window._googleStandstillOverlays = standstillMarkers;
    }
  }, [clusteredData, trackMapRef, mapProvider, deviceId, selectedVehiclePlate]);

  return (
    <div className={`map-container ${dockAlign && "qiMap-split"}`}>
      <QIMapLoader
        containerId="device-map-container"
        mapExpand={mapExpand}
        setMapRef={setTrackMapRef}
        dockAlign={dockAlign}
        maximizeMinimize={maximizeMinimize}
        mapProvider={mapProvider}
        mapboxAccessToken={mapboxAccessToken}
        googleMapsApiKey={googleMapsApiKey}
      />
      {isLoading && (
        <div className="map-loader-overlay">
          <div className="map-loader-content">
            <QISpinner size="60px" />
            <p>Loading Map...</p>
          </div>
        </div>
      )}
    </div>
  );
};
